import {
  ButtonsDropdownOption,
  Dropdown,
  dropdownTypes,
  Ellipsis,
  NewButtonSize,
  NewButtonVariant,
  Pill,
  Switch,
} from "@visiblethread/react-components";
import { DeleteItemIconButton } from "components/buttons";
import SharedByCell from "components/Cells/SharedByCell/SharedByCell";
import Flex from "components/Flex";
import { getDocumentIcon } from "components/Modal/ChooseFilesModal/utils";
import {
  deleteCollectionModal,
  deleteDocumentModal,
} from "components/Modal/confModalTypes";
import OverflowPillList from "components/OverflowPillList";
import { modalTypes } from "constants/types";
import { ReactComponent as CollectionSharedIcon } from "icons/collection-shared.svg";
import { isEqual, sortBy } from "lodash";
import {
  CircleUserRoundIcon,
  Ellipsis as EllipsisIcon,
  LayersIcon,
} from "lucide-react";
import { showModal } from "slices/app/actions/modals";
import {
  deleteCollection,
  downloadCollectionDocument,
  saveCollection,
} from "slices/collections/actions";
import { setSelected } from "slices/collections/collectionsSlice";
import { formatDateYMDToDMY, sortByDateArray } from "utils";
import { getStyles } from "utils/getStyles";

import { FILE_SOURCES } from "../documentUploader/constants";

// Collections Table
export const getCollectionsColumns = ({
  users,
  collectionTypes,
  styles,
  dispatch,
}) => [
  {
    header: "Name",
    id: "name",
    size: 250,
    minSize: 150,
    pin: "left",
    cell: ({ row }) => {
      const { name, isShared } = row.original;
      return (
        <span className={styles["collections_table__name"]}>
          {isShared ? (
            <CollectionSharedIcon height={14} width={14} />
          ) : (
            <LayersIcon height={14} width={14} />
          )}
          <Ellipsis classes="bold" text={name} />
        </span>
      );
    },
  },

  {
    header: "Categories",
    id: "categories",
    enableSorting: false,
    size: 250,
    minSize: 180,
    cell: ({ row }) => {
      const { collectionCategories = [] } = row.original;

      if (!collectionCategories.length) {
        return <Ellipsis text="-" />;
      }
      const { middleGray } = getStyles();
      const sorted = collectionTypes.filter((t) =>
        collectionCategories.some((c) => c.id === t.id)
      );
      return (
        <OverflowPillList
          items={sorted.map((cat) => ({
            id: cat.id,
            label: cat.typeName,
            color: middleGray,
          }))}
        />
      );
    },
  },
  {
    header: "Created By",
    id: "creator",
    size: 200,
    minSize: 120,
    cell: ({ row }) => {
      const { middleGray } = getStyles();

      const { creator } = row.original;
      return (
        <Pill
          leftIcon={<CircleUserRoundIcon />}
          label={creator}
          color={middleGray}
        />
      );
    },
  },
  {
    header: "No. of Docs",
    id: "docCount",
    size: 100,
    minSize: 80,
    enableSorting: false,
    cell: ({ row }) => {
      const { files } = row.original;
      const docCount = files?.length ?? 0;
      return (
        <div className={styles["collections_table__doc_count"]}>
          <Ellipsis text={docCount === 0 ? "-" : String(docCount)} />
        </div>
      );
    },
  },
  {
    header: "Shared",
    id: "isShared",
    enableSorting: false,
    size: 90,
    minSize: 80,
    cell: ({ row }) => {
      const { isShared } = row.original;
      return <Switch state={isShared} disabled labeled />;
    },
  },
  {
    header: "Shared by",
    id: "sharedWith",
    enableSorting: false,
    size: 250,
    minSize: 180,
    cell: ({ row }) => {
      const { isShared, shares, creator, isOwner, sharedOn } = row.original;
      if (!isShared || !shares?.length) {
        const { darkestGray } = getStyles();
        return <span style={{ color: darkestGray }}>None</span>;
      }
      const sharedWith = (shares || []).map((id) => {
        const user = users.find((u) => u.id === id);
        return { id, label: user?.username || String(id) };
      });
      return (
        <SharedByCell
          owner={isOwner ? "you" : creator}
          sharedOn={sharedOn ? new Date(sharedOn) : new Date()}
          sharedWith={sharedWith}
          isShared={isShared}
          isAll={sharedWith.length === users.length - 1}
        />
      );
    },
  },
  {
    header: "",
    id: "delete",
    enableSorting: false,
    enableResizing: false,
    pin: "right",
    size: 53,
    meta: {
      flexGrow: 1,
    },
    cell: ({ row }) => {
      const collection = row.original;
      if (!collection.isOwner) {
        return null;
      }
      const onClickDelete = (e) => {
        e.stopPropagation();
        const hasFiles = collection.files?.length > 0;
        if (hasFiles) {
          dispatch(
            showModal(
              modalTypes.CONFIRMATION,
              deleteCollectionModal(dispatch, collection.name, () => {
                dispatch(deleteCollection(collection.id));
              })
            )
          );
        } else {
          dispatch(deleteCollection(collection.id));
        }
      };
      return (
        <Flex justifyContent="flex-end" style={{ width: "100%" }}>
          <DeleteItemIconButton
            data-show-on-row-hover
            onClick={onClickDelete}
          />
        </Flex>
      );
    },
  },
];

// Collection Details
export const getCollectionDetailsColumns = ({
  dispatch,
  selectedCollection,
  styles,
  setChanged,
  isSharepointDownloadEnabled,
}) => [
  {
    header: "Documents",
    accessorKey: "content.name",
    id: "name",
    size: 285,
    minSize: 200,
    pin: "left",
    cell: ({ row }) => {
      const { content } = row.original;
      return (
        <div className={styles["name"]}>
          {getDocumentIcon(content.name)}
          <Ellipsis text={content.name} />
        </div>
      );
    },
  },
  {
    header: "Source",
    enableSorting: false,
    id: "source",
    size: 100,
    minSize: 80,
    cell: ({ row }) => {
      const { source } = row.original;
      return <Ellipsis text={source} />;
    },
  },
  {
    header: "Added on",
    accessorKey: "addedOn",
    id: "addedOn",
    sortDescFirst: true,
    size: 150,
    minSize: 100,
    sortingFn: sortByDateArray("addedOn"),
    cell: ({ row }) => {
      const { addedOn } = row.original;
      return <Ellipsis text={formatDateYMDToDMY(addedOn)} />;
    },
  },
  {
    header: "",
    id: "actions",
    enableSorting: false,
    enableResizing: false,
    size: 70,
    minSize: 70,
    pin: "right",
    cell: ({ row }) => {
      const doc = row.original;

      const onClickDelete = () => {
        const onOk = () => {
          const updatedCollection = {
            ...selectedCollection,
            files: selectedCollection.files.filter((f) => f.id !== doc.id),
          };
          dispatch(setSelected(updatedCollection));
          dispatch(saveCollection(updatedCollection));
          setChanged(false);
        };
        dispatch(
          showModal(
            modalTypes.CONFIRMATION,
            deleteDocumentModal(dispatch, doc.content.name, onOk)
          )
        );
      };

      const onClickDownload = () => {
        dispatch(downloadCollectionDocument(doc.id, doc.content.name));
      };

      const isSharepointSource = doc.source === FILE_SOURCES.SHAREPOINT;
      const isDownloadDisabled =
        isSharepointSource && !isSharepointDownloadEnabled;

      const options = [
        <ButtonsDropdownOption
          key="download-document"
          content="Download"
          disabled={isDownloadDisabled}
          onClick={onClickDownload}
        />,

        ...(selectedCollection.isOwner
          ? [
              <ButtonsDropdownOption
                key="delete-document"
                content="Delete"
                onClick={onClickDelete}
              />,
            ]
          : []),
      ];

      return (
        <div className={styles["actions_cell"]} data-show-on-row-hover>
          <Dropdown
            onOpenChange={(_, e) => {
              if (e) {
                e.stopPropagation();
              }
            }}
            type={dropdownTypes.buttons}
            propsObject={{
              buttonVariant: NewButtonVariant.l6,
              buttonSize: NewButtonSize.sm,
              leftIcon: <EllipsisIcon size={16} />,
              options,
              closeOnMouseLeave: true,
            }}
          />
        </div>
      );
    },
  },
];

export const getValidUserIds = (userIds, availableUsers) =>
  userIds.filter((userId) => availableUsers.some((user) => user.id === userId));

const getUsernameById = (userId, users) =>
  users.find((user) => user.id === userId)?.username || "";

export const getShareLabel = (userId, users, isOwner, currentUsername) => {
  const username = getUsernameById(userId, users);

  if (!isOwner && username === currentUsername) {
    return "you";
  }
  return username;
};

export const getShareDropdownConfig = (users, isOwner, currentUsername) => {
  const { middleGray } = getStyles();

  return {
    options: users.map((user) => ({
      value: user.id,
      label: user.username,
    })),
    color: middleGray,
    placeholder: "Add Teams or People",
    selectAllLabel: "All Users",
    getLabel: (userId) =>
      getShareLabel(userId, users, isOwner, currentUsername),
  };
};

export const getUsernames = (userIds, users) =>
  userIds.map((userId) => getUsernameById(userId, users));

export const shouldSaveShares = (originalCollection, updatedCollection) =>
  !isEqual(sortBy(originalCollection.shares), sortBy(updatedCollection.shares));

export const getCollectionTypesDropdownConfig = (collectionTypes) => {
  const { middleGray } = getStyles();
  return {
    options: collectionTypes.map((type) => ({
      value: type.id,
      label: type.typeName,
    })),
    color: middleGray,
    placeholder: "Select Categories",
    selectAllLabel: "All Categories",
    getLabel: (typeId) =>
      collectionTypes.find((type) => type.id === typeId)?.typeName || "",
  };
};

export const SHARED_COLLECTION_OWNER_DISABLED_TOOLTIP =
  "Collections can only be edited by their creator.";

export const SHARED_COLLECTION_DISABLED_TOOLTIP =
  "Sharing collections with local files is disabled.";
